import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addCartItem, updateCartItem } from "actions/cart";
import { ADD_TO_CART, REMOVE_FROM_CART, UPDATE_ITEM, OPEN_CART } from "utils/action-types";
import { trackAddToCart, trackRemoveFromCart } from "services/analytics";
import { freeGiftRemovedKey } from "utils/constants";
import { isMobile } from "utils/device-detect";
import { useGlobalContext } from "context/global";

const useCartQuantityActions = ({ product, openCart = false, onRemoved }) => {
	const dispatch = useDispatch();
	const cart = useSelector((state) => state.cart);
	const isMobileView = isMobile();
	const { isCollection, cartSettings } = useGlobalContext();

	const { variant, title = "", image = null, disableInputs } = product;
	const cartItem = cart?.items[variant.id];
	const weight = variant.weight === 0.001 ? 0 : variant.weight;

	const isFreeGift = cartSettings.bonus?.product && variant.id === cartSettings.bonus.product.variant.id;
	const shouldNotOpenCart = (isCollection && isMobileView) || !openCart;

	const addProduct = useCallback(() => {
		if (isFreeGift) return;

		dispatch({
			type: ADD_TO_CART,
			payload: {
				id: variant.id,
				totalPrice: variant.price,
				title,
				image: { src: image, altText: title },
				quantity: 1,
				weight,
				shouldNotOpenCart,
			},
		});

		dispatch(addCartItem(variant.id, variant.stock === "one-time"));
		trackAddToCart({ ...product, quantity: 1 });

		if (openCart) {
			dispatch({ type: OPEN_CART });
		}
	}, [dispatch, variant, title, image, weight, shouldNotOpenCart, product, isFreeGift, openCart]);

	const updateQuantity = useCallback(
		(qty) => {
			const isIncreasing = qty > 0;
			if ((isFreeGift && isIncreasing) || disableInputs) return;

			const quantity = cartItem.quantity + qty;
			const totalQuantity = cart?.totalQuantity + qty;
			const totalProgress = cart?.totalProgress + (isIncreasing ? weight : -weight);

			if (quantity === 0) {
				dispatch({ type: REMOVE_FROM_CART, id: variant.id, weight });
				if (isFreeGift) {
					sessionStorage.setItem(freeGiftRemovedKey, variant.id);
				}
				onRemoved?.();
			} else {
				dispatch({
					type: UPDATE_ITEM,
					id: variant.id,
					quantity,
					totalQuantity,
					totalProgress,
					shouldNotOpenCart,
				});
			}

			dispatch(updateCartItem(cartItem?.tempId, quantity));

			isIncreasing
				? trackAddToCart({ ...product, quantity: 1 })
				: trackRemoveFromCart({ ...product, quantity: 1 });
		},
		[cartItem, dispatch, product, variant.id, weight, onRemoved, isFreeGift, shouldNotOpenCart]
	);

	return {
		cartItem,
		addProduct,
		updateQuantity,
		isFreeGift,
		weight,
	};
};

export default useCartQuantityActions;